import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  Legend
} from 'recharts'

const FOREST = '#2f5d3a'
const AMBER = '#d97706'
const GRAY = '#6b7280'
const GREEN = '#16a34a'
const RED = '#dc2626'
const BLUE = '#2563eb'
const PURPLE = '#7c3aed'

interface ChartCardProps {
  title: string
  subtitle?: string
  height?: number
  empty?: boolean
  emptyMessage?: string
  children: React.ReactNode
}

export function ChartCard({ title, subtitle, height = 280, empty, emptyMessage = 'Nothing to chart yet.', children }: ChartCardProps) {
  return (
    <div className="bg-white rounded-xl border border-chestnut-gray p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-chestnut-charcoal">{title}</h2>
        {subtitle && <p className="text-xs text-chestnut-charcoal/60 mt-0.5">{subtitle}</p>}
      </div>
      {empty ? (
        <div className="flex items-center justify-center text-sm text-chestnut-charcoal/50" style={{ height }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer>
            {children as React.ReactElement}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

// Recharts types a tooltip value loosely: a single number/string, an array (for
// stacked or range series) or undefined before any point is hovered. Our
// formatters all want a plain number, so narrow once here instead of lying to
// the compiler about what Recharts actually passes in.
type TooltipValue = number | string | ReadonlyArray<number | string> | undefined
type TooltipName = number | string | undefined

function asNumber(value: TooltipValue): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function fmtMoney(n: number) {
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`
  return `$${n.toFixed(0)}`
}

function fmtMoneyFull(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

interface SimpleBarProps {
  data: { label: string; value: number }[]
  color?: string
  valueFormatter?: (n: number) => string
  tooltipLabel?: string
}

export function SimpleBarChart({ data, color = FOREST, valueFormatter = fmtMoneyFull, tooltipLabel = 'Value' }: SimpleBarProps) {
  return (
    <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtMoney} />
      <Tooltip
        formatter={(v: TooltipValue) => [valueFormatter(asNumber(v)), tooltipLabel]}
        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
      />
      <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
    </BarChart>
  )
}

interface StackedBarProps {
  data: Array<Record<string, string | number>>
  keys: { key: string; label: string; color: string }[]
  xKey?: string
  valueFormatter?: (n: number) => string
}

export function StackedBarChart({ data, keys, xKey = 'label', valueFormatter = (n) => n.toLocaleString() }: StackedBarProps) {
  return (
    <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
      <YAxis tick={{ fontSize: 11 }} tickFormatter={(n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`} />
      <Tooltip
        formatter={(v: TooltipValue, name: TooltipName) => [valueFormatter(asNumber(v)), name ?? '']}
        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {keys.map(k => (
        <Bar key={k.key} dataKey={k.key} stackId="a" name={k.label} fill={k.color} />
      ))}
    </BarChart>
  )
}

interface FunnelChartProps {
  steps: { label: string; value: number; color?: string }[]
}

const FUNNEL_DEFAULT_COLORS = [FOREST, AMBER, GRAY]

export function FunnelChart({ steps }: FunnelChartProps) {
  const max = Math.max(...steps.map(s => s.value), 1)
  return (
    <div className="flex flex-col gap-3 py-2">
      {steps.map((s, i) => {
        const pct = (s.value / max) * 100
        const color = s.color || FUNNEL_DEFAULT_COLORS[i % FUNNEL_DEFAULT_COLORS.length]
        const conv = i > 0 && steps[0].value > 0
          ? ` (${Math.round((s.value / steps[0].value) * 100)}% of ${steps[0].label})`
          : ''
        return (
          <div key={s.label} className="flex items-center gap-3">
            <div className="w-32 text-sm text-chestnut-charcoal/80 text-right shrink-0">{s.label}</div>
            <div className="flex-1 bg-chestnut-gray/30 rounded-md overflow-hidden h-9 relative">
              <div
                className="h-full flex items-center px-3 text-white text-sm font-medium transition-all"
                style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }}
              >
                {s.value.toLocaleString()}
              </div>
            </div>
            <div className="w-44 text-xs text-chestnut-charcoal/60 shrink-0">{conv}</div>
          </div>
        )
      })}
    </div>
  )
}

interface ColoredBarProps {
  data: { label: string; value: number; color?: string }[]
  valueFormatter?: (n: number) => string
  tooltipLabel?: string
}

export function ColoredBarChart({ data, valueFormatter = (n) => n.toLocaleString(), tooltipLabel = 'Count' }: ColoredBarProps) {
  return (
    <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
      <Tooltip
        formatter={(v: TooltipValue) => [valueFormatter(asNumber(v)), tooltipLabel]}
        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
      />
      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
        {data.map((d, i) => (
          <Cell key={i} fill={d.color || FOREST} />
        ))}
      </Bar>
    </BarChart>
  )
}

interface TimeSeriesBarProps {
  data: { label: string; value: number }[]
  color?: string
  valueFormatter?: (n: number) => string
  tooltipLabel?: string
}

export function TimeSeriesBarChart({ data, color = FOREST, valueFormatter = (n) => n.toLocaleString(), tooltipLabel = 'Count' }: TimeSeriesBarProps) {
  return (
    <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" angle={-30} textAnchor="end" height={60} />
      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
      <Tooltip
        formatter={(v: TooltipValue) => [valueFormatter(asNumber(v)), tooltipLabel]}
        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
      />
      <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
    </BarChart>
  )
}

export const ChartColors = { FOREST, AMBER, GRAY, GREEN, RED, BLUE, PURPLE }
